// editInterestedParty.ts

// Import necessary modules and types
import { auth, db, FirebaseFieldValue, firebaseImport } from '../../config/main_config';
import { createToast } from './createToast.util';
import { closeModal } from './closeModal.util';
import { InterestedParty, InterestedPartiesRegister as InterestedParties, } from '../../../../interfaces/interestedParties.interface';
import { openModal } from './openModal.util';
import { waitForCompanyID } from './waitForCompanyID.util';
// Interface representing the ISMS manual document structure
interface ISMSManual {
    partiesInterested?: InterestedParties;
    [key: string]: any;
    // Additional properties can be added as needed
}

// Interface representing a history record for an interested party
export interface InterestedPartyHistory {
    timestamp: string;
    data: InterestedParty;
}

/**
 * Generates a formatted timestamp string.
 * Format: DD_MM_YYYY@HH:MM:SS_AM/PM
 * Example: 14_10_2024@03:45:30_PM
 * 
 * @returns A formatted timestamp string.
 */
function getFormattedTimestamp(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
    };
    const timeString = now.toLocaleString('en-US', options).replace(/\s/g, ''); // 'HH:MM:SS_AM/PM'

    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is zero-based
    const year = now.getFullYear();

    return `${day}_${month}_${year}@${timeString}`;
}

/**
 * Saves the current state of an interested party to the history subcollection.
 * 
 * @param companyID - The company identifier.
 * @param partyID - The ID of the interested party.
 * @param currentPartyData - The current data of the interested party.
 */
async function updateInterestedPartyHistory(
    companyID: string,
    partyID: string,
    currentPartyData: InterestedParty
): Promise<void> {
    const timestamp = getFormattedTimestamp();
    const historyRef = db
        .collection(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual/partiesInterested/${partyID}/history`)
        .doc(timestamp);

    const historyRecord: InterestedPartyHistory = {
        timestamp,
        data: currentPartyData
    };

    try {
        await historyRef.set(historyRecord);
        console.log(`Interested party history saved at ${timestamp}`);
    } catch (error) {
        console.error('Error saving interested party history:', error);
    }
}

// Function to delete an interested party
async function deleteInterestedParty(companyID: string, partyID: string): Promise<void> {
    const managementRegisterRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/interestedPartiesRegister/register`);
    const ismsManualRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);

    try {
        // Fetch the current state of the interested parties
        const managementSnapshot = await managementRegisterRef.get();
        const ismsSnapshot = await ismsManualRef.get();

        let currentManagementData: InterestedParties = managementSnapshot.exists ? managementSnapshot.data() : {};
        let currentISMSData: ISMSManual = ismsSnapshot.exists ? ismsSnapshot.data() : {};

        // Get the interested party to be deleted
        const deletedParty = currentManagementData[partyID] || currentISMSData.partiesInterested?.[partyID];

        if (deletedParty) {
            // Save the current state to history before deleting
            await updateInterestedPartyHistory(companyID, partyID, deletedParty);

            // Delete the interested party from both documents
            delete currentManagementData[partyID];
            if (currentISMSData.partiesInterested) {
                delete currentISMSData.partiesInterested[partyID];
            }

            // Update Firestore documents
            await managementRegisterRef.set(currentManagementData);
            await ismsManualRef.set(currentISMSData);

            console.log('Interested party deleted successfully:', partyID);
        } else {
            console.error('Interested party not found in the current data.');
        }
    } catch (error) {
        console.error('Error deleting interested party:', error);
    }
}

// Function to handle delete interested party button click
export async function deleteInterestedPartyHandler(event: Event, partyID: string): Promise<void> {
    event.preventDefault();

    const companyID = await waitForCompanyID(); // Assume this function fetches the current company ID

    if (companyID && partyID) {
        // Confirm deletion with the user
        const confirmation = confirm('Are you sure you want to delete this interested party? This action cannot be undone.');
        if (confirmation) {
            await deleteInterestedParty(companyID, partyID);
            createToast({
                message: 'Interested party deleted successfully.'
            });
            // Remove the deleted party's rows from the table
            const partyRow = document.querySelector(`tr.tableordion-row[data-item-id="${partyID}"]`);
            const partyContentRow = document.querySelector(`tr.tableordion-content[data-item-id="${partyID}"]`);
            if (partyRow) {
                partyRow.remove();
            }
            if (partyContentRow) {
                partyContentRow.remove();
            }
        }
    } else {
        createToast({
            message: 'An error occurred when trying to delete the interested party.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }
}

// Make deleteInterestedPartyHandler available globally
(window as any).deleteInterestedPartyHandler = deleteInterestedPartyHandler;

export async function getInterestedPartyData(companyID: string, partyID: string): Promise<InterestedParty | null> {
    const managementRegisterRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/interestedPartiesRegister/register`);
    const ismsManualRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);

    try {
        // First try fetching from the managementSystemGuide path
        const managementDocSnapshot = await managementRegisterRef.get();
        if (managementDocSnapshot.exists) {
            const data = managementDocSnapshot.data()?.[partyID];
            if (data) {
                console.log(`Retrieved interested party data for ID ${partyID} from management register.`);
                return data as InterestedParty;
            }
        }

        // If not found, try fetching from the ismsManual path
        const ismsDocSnapshot = await ismsManualRef.get();
        if (ismsDocSnapshot.exists) {
            const data = ismsDocSnapshot.data()?.partiesInterested?.[partyID];
            if (data) {
                console.log(`Retrieved interested party data for ID ${partyID} from ISMS manual.`);
                return data as InterestedParty;
            }
        }

        console.error(`Interested party with ID ${partyID} not found in both locations.`);
        return null;

    } catch (error) {
        console.error('Error fetching interested party data:', error);
        return null;
    }
}

/**
 * Opens the edit modal and populates it with the selected interested party's data.
 * 
 * @param partyID - The ID of the interested party to edit.
 * @param companyID - The company identifier.
 */
export async function openEditInterestedPartyModal(partyID: string): Promise<void> {
    let companyID = await waitForCompanyID();
    // Retrieve the existing data of the interested party
    const partyData = await getInterestedPartyData(companyID, partyID);
    console.log(`Party Data retrieved for ID ${partyID}:`, partyData);

    if (!partyData) {
        createToast({
            message: 'Unable to retrieve interested party data.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    // Populate the modal input fields with the current data
    const dateAddedEditElement = document.getElementById('dateAdded_edit') as HTMLInputElement;
    const dateUpdatedEditElement = document.getElementById('dateUpdated_edit') as HTMLInputElement;
    const partyNameElement = document.getElementById('partyName_edit') as HTMLInputElement;
    const requirementElement = document.getElementById('requirement_edit') as HTMLInputElement;
    const requirementsAddressedElement = document.getElementById('requirementsAddressed_edit') as HTMLInputElement;
    // const updatedByElement = document.getElementById('updatedBy_edit') as HTMLInputElement;
    const frameworksElement = document.getElementById('frameworks_edit') as HTMLInputElement;

    if (!dateAddedEditElement || !partyNameElement || !requirementElement ||
        !requirementsAddressedElement || !frameworksElement) {
        console.error('One or more modal input elements not found.');
        return;
    }

    // Format dates if they exist
    const dateAdded = partyData.dateAdded instanceof firebaseImport.firestore.Timestamp
        ? partyData.dateAdded.toDate()
        : '';
    const dateUpdated = partyData.dateUpdated instanceof firebaseImport.firestore.Timestamp
        ? partyData.dateUpdated.toDate()
        : '';

    // Populate the input fields
    dateAddedEditElement.valueAsDate = dateAdded || new Date();
    dateUpdatedEditElement.valueAsDate = dateUpdated || new Date();
    partyNameElement.value = partyData.party || '';
    requirementElement.value = partyData.requirement || '';
    requirementsAddressedElement.value = partyData.requirementsAddressedThroughISMS || '';
    // updatedByElement.value = partyData.updatedBy || '';
    frameworksElement.value = partyData.frameworks ? partyData.frameworks.map(control => (control as any).standard).join(', ') : '';
    window.selectedControls = partyData.frameworks || [];

    // Set the current party ID and company ID in the modal's dataset for later retrieval
    const editModal = document.querySelector("[data-managedid='EditInterestedPartyModal']") as HTMLElement;
    if (editModal) {
        editModal.setAttribute('data-current-party-id', partyID);
        // editModal.style.display = 'block';
    } else {
        console.error('Edit Interested Party Modal not found.');
    }

    // Display the modal (assuming you're using Bootstrap)
    // $('#EditInterestedPartyModal').modal('show');
    openModal('[data-managedid="EditInterestedPartyModal"]');
}

// /**
//  * Saves the edited interested party data to Firestore.
//  * 
//  * @param event - The event triggered by the save button.
//  */
// export async function saveEditedInterestedParty(event: Event): Promise<void> {
//     event.preventDefault();

//     // Retrieve the modal element
//     const editModal = document.querySelector("[data-managedid='EditInterestedPartyModal']") as HTMLElement;
//     if (!editModal) {
//         console.error('Edit Interested Party Modal not found.');
//         return;
//     }

//     // Retrieve the current party ID and company ID from the modal's dataset
//     const partyID = editModal.getAttribute('data-current-party-id');
//     const companyID = await waitForCompanyID();

//     if (!partyID || !companyID) {
//         console.error('Party ID or Company ID not found in modal dataset.');
//         createToast({
//             message: 'An error occurred. Please try again.',
//             customStyles: {
//                 backgroundColor: '#ff0000',
//                 color: '#ffffff'
//             }
//         });
//         return;
//     }

//     // Retrieve input elements
//     const dateAddedEditElement = document.getElementById('dateAdded_edit') as HTMLInputElement;
//     const partyNameElement = document.getElementById('partyName_edit') as HTMLInputElement;
//     const requirementElement = document.getElementById('requirement_edit') as HTMLInputElement;
//     const requirementsAddressedElement = document.getElementById('requirementsAddressed_edit') as HTMLInputElement;
//     // const updatedByElement = document.getElementById('updatedBy_edit') as HTMLInputElement;
//     const frameworksElement = document.getElementById('frameworks_edit') as HTMLInputElement;

//     // Ensure all elements are found
//     if (!dateAddedEditElement || !partyNameElement || !requirementElement ||
//         !requirementsAddressedElement || !frameworksElement) {
//         console.error('One or more input elements not found in the modal.');
//         createToast({
//             message: 'An error occurred. Please try again.',
//             customStyles: {
//                 backgroundColor: '#ff0000',
//                 color: '#ffffff'
//             }
//         });
//         return;
//     }

//     // Extract values from input fields
//     const dateAdded = dateAddedEditElement.value;
//     const partyName = partyNameElement.value.trim();
//     const requirement = requirementElement.value.trim();
//     const requirementsAddressed = requirementsAddressedElement.value.trim();
//     const frameworks = frameworksElement.value.split(',').map(fw => fw.trim()).filter(fw => fw !== '');

//     // Validate required fields
//     if (!partyName || !requirement) {
//         createToast({
//             message: 'Party Name and Requirement are required fields.',
//             customStyles: {
//                 backgroundColor: '#ffcc00',
//                 color: '#000000'
//             }
//         });
//         return;
//     }

//     // Reference to the interested party document
//     const partyDocRef = db.doc(
//         `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual/`
//     );

//     try {
//         // Fetch the current state of the interested party
//         const docSnapshot = await partyDocRef.get();
//         let currentPartyData: InterestedParty = {};

//         if (docSnapshot.exists) {
//             currentPartyData = docSnapshot.data()?.partiesInterested[partyID] as InterestedParty;

//             // Save the current state to history before making changes
//             await updateInterestedPartyHistory(companyID, partyID, currentPartyData);
//         } else {
//             console.error('Interested party document does not exist.');
//             createToast({
//                 message: 'Unable to find the interested party. Please try again.',
//                 customStyles: {
//                     backgroundColor: '#ff0000',
//                     color: '#ffffff'
//                 }
//             });
//             return;
//         }

//         // Prepare the updated data
//         const updatedParty: InterestedParty = {
//             ...currentPartyData, // Retain existing data not being updated
//             dateAdded: firebaseImport.firestore.Timestamp.fromDate(new Date(dateAdded)),
//             dateUpdated: FirebaseFieldValue.serverTimestamp() as firebaseImport.firestore.Timestamp,
//             party: partyName,
//             requirement: requirement,
//             requirementsAddressedThroughISMS: requirementsAddressed,
//             updatedBy: auth.currentUser.uid,
//             frameworks: frameworks
//         };

//         // Update the interested party document
//         await partyDocRef.update(updatedParty);

//         console.log('Interested party edited successfully:', updatedParty);

//         // Provide user feedback
//         createToast({
//             message: 'Interested party edited successfully.'
//         });

//         // Close the modal
//         closeModal(null, true); // Assuming closeModal handles closing the modal
//     } catch (error) {
//         console.error('Error editing interested party:', error);
//         createToast({
//             message: 'An error occurred while saving changes.',
//             customStyles: {
//                 backgroundColor: '#ff0000',
//                 color: '#ffffff'
//             }
//         });
//     }
// }

export async function saveEditedInterestedParty(event: Event): Promise<void> {
    event.preventDefault();

    // Retrieve the modal element
    const editModal = document.querySelector("[data-managedid='EditInterestedPartyModal']") as HTMLElement;
    if (!editModal) {
        console.error('Edit Interested Party Modal not found.');
        return;
    }

    // Retrieve the current party ID and company ID from the modal's dataset
    const partyID = editModal.getAttribute('data-current-party-id');
    const companyID = await waitForCompanyID();

    if (!partyID || !companyID) {
        console.error('Party ID or Company ID not found in modal dataset.');
        createToast({
            message: 'An error occurred. Please try again.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    // Retrieve input elements
    const dateAddedEditElement = document.getElementById('dateAdded_edit') as HTMLInputElement;
    const partyNameElement = document.getElementById('partyName_edit') as HTMLInputElement;
    const requirementElement = document.getElementById('requirement_edit') as HTMLInputElement;
    const requirementsAddressedElement = document.getElementById('requirementsAddressed_edit') as HTMLInputElement;
    const frameworksElement = document.getElementById('frameworks_edit') as HTMLInputElement;

    if (!dateAddedEditElement || !partyNameElement || !requirementElement ||
        !requirementsAddressedElement || !frameworksElement) {
        console.error('One or more input elements not found in the modal.');
        createToast({
            message: 'An error occurred. Please try again.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    // Extract values from input fields
    const dateAdded = dateAddedEditElement.value;
    const partyName = partyNameElement.value.trim();
    const requirement = requirementElement.value.trim();
    const requirementsAddressed = requirementsAddressedElement.value.trim();
    const frameworks = window?.selectedControls !== undefined ? window.selectedControls : frameworksElement.value.split(',').map(fw => fw.trim()).filter(fw => fw !== '');

    if (!partyName || !requirement) {
        createToast({
            message: 'Party Name and Requirement are required fields.',
            customStyles: {
                backgroundColor: '#ffcc00',
                color: '#000000'
            }
        });
        return;
    }

    // Prepare the updated data
    const updatedParty: InterestedParty = {
        dateAdded: firebaseImport.firestore.Timestamp.fromDate(new Date(dateAdded)),
        dateUpdated: FirebaseFieldValue.serverTimestamp() as firebaseImport.firestore.Timestamp,
        party: partyName,
        requirement: requirement,
        requirementsAddressedThroughISMS: requirementsAddressed,
        updatedBy: auth.currentUser.uid,
        frameworks: frameworks
    };

    try {
        // Save to both locations
        const managementRegisterRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/interestedPartiesRegister/register`);
        const ismsManualRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);

        // First, fetch the current party data
        const docSnapshot = await managementRegisterRef.get();
        let currentRegister: InterestedParties = docSnapshot.exists ? docSnapshot.data() : {};

        // Update the history before making changes
        await updateInterestedPartyHistory(companyID, partyID, currentRegister[partyID]);

        // Update both Firestore documents
        await managementRegisterRef.update({
            [`${partyID}`]: updatedParty
        });

        await ismsManualRef.update({
            [`partiesInterested.${partyID}`]: updatedParty
        });

        console.log('Interested party edited and saved successfully in both locations.');

        // Provide user feedback
        createToast({
            message: 'Interested party edited successfully.'
        });

        closeModal(null, true); // Close the modal

    } catch (error) {
        console.error('Error editing interested party:', error);
        createToast({
            message: 'An error occurred while saving changes.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}